<template>
  <div
    class="p-5 w-full h-full bg-gray-50 overflow-y-auto"
    data-cy="close-cashfloat-v2"
  >
    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-40 text-muted-foreground"
    >
      {{ t("common.loading") }}
    </div>

    <template v-else>
      <!-- Header -->
      <div class="px-8 pt-6 pb-4 flex items-start justify-between gap-4">
        <div>
          <p class="text-2xl font-bold">
            {{ t("cashfloat.sale-closure") }}
          </p>
          <p class="text-sm text-muted-foreground mt-1">
            {{ t("cashfloat.v2.close.session-opened-on") }}
            <span class="font-medium text-foreground">
              {{ sessionStartDate }}
            </span>
            {{ t("cashfloat.v2.close.by") }}
            <span class="font-medium text-foreground">
              {{ openedByName }}
            </span>
          </p>
        </div>
        <div class="flex items-center gap-3 shrink-0">
          <div class="relative group">
            <SButton
              type="button"
              variant="outline"
              size="sm"
              class="!rounded-md"
              :disabled="!isOnline"
              data-cy="v2-close-add-transfer"
              @click="addCashTransfer"
            >
              <ArrowLeftRightIcon class="w-4 h-4 mr-2" />
              {{ t("cashfloat.v2.close.add-cash-transfer") }}
            </SButton>
            <div
              v-if="!isOnline"
              class="pointer-events-none absolute right-0 top-full mt-2 z-20 hidden group-hover:block w-64 rounded-md bg-slate-800 text-white text-xs px-3 py-2 shadow-lg"
              data-cy="v2-close-add-transfer-offline-tooltip"
            >
              {{ t("cashtransfer.v2.offline-tooltip") }}
            </div>
          </div>
          <SButton
            type="button"
            size="sm"
            class="!rounded-md"
            data-cy="v2-close-open-drawer"
            @click="openCashDrawer"
          >
            <BanknoteIcon class="w-4 h-4 mr-2" />
            {{ t("cashfloat.v2.close.open-cash-drawer") }}
          </SButton>
        </div>
      </div>

      <!-- Sync warning -->
      <div
        v-if="hasUnsyncedOrders"
        class="mx-8 mb-4 flex items-start gap-3 bg-amber-50/70 border border-amber-300 text-amber-900 rounded-md px-4 py-3"
        data-cy="v2-close-sync-warning"
      >
        <InfoIcon class="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
        <p class="text-sm">
          <span class="font-semibold">
            {{ t("cashfloat.v2.close.orders-not-synced") }}
          </span>
          {{ t("cashfloat.v2.close.orders-not-synced-help") }}
        </p>
      </div>

      <!-- Tabs -->
      <div class="flex justify-center mb-4">
        <div
          class="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1"
        >
          <SButton
            type="button"
            variant="ghost"
            size="sm"
            class="px-4 py-1.5 rounded-md transition border"
            :class="
              activeTab === 'manual'
                ? 'text-emerald-700 border-emerald-500 shadow-sm'
                : 'text-muted-foreground border-transparent hover:bg-slate-50'
            "
            data-cy="v2-close-tab-manual"
            @click="activeTab = 'manual'"
          >
            {{ t("cashfloat.v2.close.manual-reconciliation") }}
          </SButton>
          <SButton
            type="button"
            variant="ghost"
            size="sm"
            class="px-4 py-1.5 rounded-md transition border"
            :class="
              activeTab === 'auto'
                ? 'text-emerald-700 border-emerald-500 shadow-sm'
                : 'text-muted-foreground border-transparent hover:bg-slate-50'
            "
            data-cy="v2-close-tab-auto"
            @click="activeTab = 'auto'"
          >
            {{ t("cashfloat.digital-methods") }}
          </SButton>
        </div>
      </div>

      <!-- Payment method tabs — cash + any other manual method the backend returns. -->
      <div
        v-if="activeTab === 'manual' && paymentMethods.length > 1"
        class="flex justify-center mb-4"
      >
        <div
          class="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 overflow-x-auto"
        >
          <SButton
            v-for="pm in paymentMethods"
            :key="pm.name"
            type="button"
            variant="ghost"
            size="sm"
            class="px-4 py-1.5 rounded-md transition border shrink-0"
            :class="
              selectedPaymentMethod === pm.name
                ? 'text-emerald-700 border-emerald-500 shadow-sm'
                : 'text-muted-foreground border-transparent hover:bg-slate-50'
            "
            :data-cy="`v2-close-payment-method-${pm.name}`"
            @click="selectPaymentMethod(pm.name)"
          >
            {{ t(`payment-method.${pm.name}`) }}
          </SButton>
        </div>
      </div>

      <!-- Manual reconciliation: details (left) + currency cards (right) -->
      <div
        v-if="activeTab === 'manual'"
        class="px-8 grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4"
      >
        <!-- Left: selected payment method / currency details -->
        <SCard
          v-if="selectedPaymentSummary && selectedCurrency"
          class="!gap-0 !py-0 px-0 self-start"
        >
          <div
            class="flex items-center justify-between px-8 py-4 border-b border-slate-100"
          >
            <div class="flex items-center gap-2">
              <span
                class="inline-flex items-center justify-center min-w-8 h-8 px-1.5 rounded-md bg-slate-100 text-xs font-bold text-[#044c55]"
              >
                {{ selectedCurrency }}
              </span>
              <p class="font-semibold">
                {{
                  t("payment-method-in-currency", {
                    method: t(`payment-method.${selectedPaymentMethod}`),
                    currency: selectedCurrency,
                  })
                }}
              </p>
            </div>
            <SButton
              v-if="selectedPaymentMethod === PaymentMethods.METHOD_POS_CASH"
              type="button"
              variant="ghost"
              size="sm"
              class="inline-flex items-center gap-1 text-xs font-medium text-[#044c55] bg-sky-50 px-2 py-1 rounded-md"
              data-cy="v2-close-cash-inventory"
              @click="openCashInventory(selectedCurrency)"
            >
              <LayoutGridIcon class="w-3 h-3" />
              {{ t("cashfloat.v2.close.cash-inventory") }}
            </SButton>
          </div>
          <div class="divide-y divide-slate-100 text-sm">
            <DetailsRow
              data-cy="v2-close-detail-initial-value"
              :label="t('cashfloat.v2.close.initial-value')"
              :value="
                formatMoney(
                  getInitialAmount(selectedPaymentSummary, selectedCurrency),
                  selectedCurrency
                )
              "
            />
            <DetailsRow
              data-cy="v2-close-detail-payments"
              :label="t('cashfloat.payments')"
              :value="
                formatMoney(
                  getCustomerAmountIn(selectedPaymentSummary, selectedCurrency),
                  selectedCurrency
                )
              "
            />
            <DetailsRow
              data-cy="v2-close-detail-refunds"
              :label="t('cashfloat.refunds')"
              :value="
                formatMoney(
                  getRefund(selectedPaymentSummary, selectedCurrency),
                  selectedCurrency
                )
              "
              :negative="
                getRefund(selectedPaymentSummary, selectedCurrency) !== 0
              "
            />
            <DetailsRow
              data-cy="v2-close-detail-change"
              :label="t('cashfloat.change')"
              :value="
                formatMoney(
                  getChange(selectedPaymentSummary, selectedCurrency),
                  selectedCurrency
                )
              "
              :negative="
                getChange(selectedPaymentSummary, selectedCurrency) !== 0
              "
            />
            <DetailsRow
              data-cy="v2-close-detail-settlement"
              :label="t('cashfloat.settlement')"
              :value="
                formatMoney(
                  getSettlement(selectedPaymentSummary, selectedCurrency),
                  selectedCurrency
                )
              "
              bold
            />
            <DetailsRow
              v-if="selectedPaymentMethod === PaymentMethods.METHOD_POS_CASH"
              data-cy="v2-close-detail-transfer-in"
              :label="t('cashfloat.transfer-in')"
              :value="
                formatMoney(
                  getTransferIn(selectedPaymentSummary, selectedCurrency),
                  selectedCurrency
                )
              "
            />
            <DetailsRow
              v-if="selectedPaymentMethod === PaymentMethods.METHOD_POS_CASH"
              data-cy="v2-close-detail-transfer-out"
              :label="t('cashfloat.transfer-out')"
              :value="
                formatMoney(
                  getTransferOut(selectedPaymentSummary, selectedCurrency),
                  selectedCurrency
                )
              "
              :negative="
                getTransferOut(selectedPaymentSummary, selectedCurrency) !== 0
              "
            />
            <DetailsRow
              data-cy="v2-close-detail-expected-total"
              :label="t('cashfloat.v2.close.expected-total')"
              :value="
                formatMoney(
                  getExpectedAmount(selectedPaymentSummary, selectedCurrency),
                  selectedCurrency
                )
              "
              bold
            />
            <DetailsRow
              data-cy="v2-close-detail-counted-amount"
              :label="t('cashfloat.counted-amount')"
              :value="
                formatMoney(
                  getCountedAmount(selectedPaymentSummary, selectedCurrency),
                  selectedCurrency
                )
              "
              bold
            />
            <DetailsRow
              data-cy="v2-close-detail-difference"
              :label="t('cashfloat.v2.difference')"
              :value="differenceLabel(selectedPaymentSummary, selectedCurrency)"
              :negative="
                getDifference(selectedPaymentSummary, selectedCurrency) !== 0
              "
              bold
            />
            <DetailsRow
              data-cy="v2-close-detail-note"
              :label="t('cashfloat.note')"
              :value="getNote(selectedPaymentSummary, selectedCurrency) || '—'"
            />
          </div>
        </SCard>

        <!-- Right: per-currency cards -->
        <div class="flex flex-col gap-4">
          <SCard
            v-for="currency in currenciesForSelectedMethod"
            :key="currency"
            class="!gap-2 !py-4 px-4 cursor-pointer hover:shadow-sm transition"
            :class="
              focusedCurrency === currency
                ? '!border-2 !border-[#044c55] shadow-md'
                : selectedCurrency === currency
                ? '!border-2 !border-[#044c55]/40'
                : ''
            "
            :data-cy="`v2-close-currency-${currency}`"
            @click="selectedCurrency = currency"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex items-center justify-center min-w-8 h-8 px-1.5 rounded-md bg-slate-100 text-xs font-bold text-[#044c55]"
                >
                  {{ currency }}
                </span>
                <p class="font-semibold">
                  {{
                    t("payment-method-in-currency", {
                      method: t(`payment-method.${selectedPaymentMethod}`),
                      currency: currency,
                    })
                  }}
                </p>
              </div>
              <span
                class="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full"
                :class="
                  isMatch(currency)
                    ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
                    : 'text-orange-700 bg-orange-50 border border-orange-200'
                "
              >
                <CheckIcon v-if="isMatch(currency)" class="w-3 h-3" />
                <TriangleAlertIcon v-else class="w-3 h-3" />
                {{
                  isMatch(currency)
                    ? t("cashfloat.match")
                    : t("cashfloat.mismatch")
                }}
              </span>
            </div>
            <div class="flex items-start justify-between text-sm">
              <div>
                <p class="text-muted-foreground">
                  {{ t("cashfloat.v2.close.initial-value") }}
                </p>
                <p>
                  {{
                    selectedPaymentSummary
                      ? formatMoney(
                          getInitialAmount(selectedPaymentSummary, currency),
                          currency
                        )
                      : `${currency} 0.00`
                  }}
                </p>
              </div>
              <div class="text-right">
                <p class="text-muted-foreground">
                  {{ t("cashfloat.v2.difference") }}
                </p>
                <p
                  :class="
                    selectedPaymentSummary &&
                    getDifference(selectedPaymentSummary, currency) !== 0
                      ? getDifference(selectedPaymentSummary, currency) > 0
                        ? 'text-[#044c55] font-medium'
                        : 'text-red-600 font-medium'
                      : ''
                  "
                >
                  {{
                    selectedPaymentSummary
                      ? differenceLabel(selectedPaymentSummary, currency)
                      : `${currency} 0.00`
                  }}
                </p>
              </div>
            </div>
            <div>
              <p class="text-sm text-muted-foreground mb-1">
                {{ t("cashfloat.counted-amount") }}
              </p>
              <SInputGroup>
                <SInputGroupInput
                  v-model="
                    countedInput[countedKey(selectedPaymentMethod, currency)]
                  "
                  type="number"
                  :min="0"
                  placeholder="0.00"
                  :data-cy="`v2-close-counted-${currency}`"
                  @input="onCountedInput(currency)"
                  @focus="onCountedFocus(currency)"
                  @blur="onCountedBlur(currency)"
                />
                <SInputGroupAddon align="inline-end">
                  <SInputGroupText class="font-semibold">{{
                    currency
                  }}</SInputGroupText>
                </SInputGroupAddon>
              </SInputGroup>
            </div>

            <!-- Difference counterparty — a counted mismatch posts as a normal
                 two-ended transfer (surplus flows in from the selected
                 location, deficit flows out to it), so the cashier must pick
                 the counterparty. Cash on a linked device only. -->
            <div
              v-if="
                selectedPaymentMethod === PaymentMethods.METHOD_POS_CASH &&
                hardwareLocId != null &&
                !isMatch(currency)
              "
              class="flex flex-col gap-1 bg-sky-50/60 rounded-md p-3"
              :data-cy="`v2-close-difference-panel-${currency}`"
              @click.stop
            >
              <label class="text-sm font-medium">
                {{
                  selectedPaymentSummary &&
                  getDifference(selectedPaymentSummary, currency) > 0
                    ? t("cashfloat.v2.from")
                    : t("cashfloat.v2.to")
                }}
                <span class="text-red-500">*</span>
              </label>
              <SSelect
                v-model="differenceLocationByCurrency[currency]"
                :data-cy="`v2-close-difference-location-${currency}`"
              >
                <SSelectTrigger class="w-full bg-white text-left">
                  <component
                    v-if="selectedDifferenceLocation(currency)"
                    :is="selectedDifferenceLocation(currency)?.icon"
                    class="w-4 h-4 mr-2"
                    :class="selectedDifferenceLocation(currency)?.colorClass"
                  />
                  <SSelectValue
                    :placeholder="
                      selectedPaymentSummary &&
                      getDifference(selectedPaymentSummary, currency) > 0
                        ? t('cashfloat.v2.select-extra-source')
                        : t('cashfloat.v2.select-missing-destination')
                    "
                  />
                </SSelectTrigger>
                <SSelectContent>
                  <SSelectItem
                    v-for="loc in richLocationOptions"
                    :key="loc.value"
                    :value="loc.value"
                  >
                    <span class="flex items-center gap-2">
                      <span
                        class="inline-flex items-center justify-center w-6 h-6 rounded-md"
                        :class="loc.bgClass"
                      >
                        <component
                          :is="loc.icon"
                          class="w-3.5 h-3.5"
                          :class="loc.colorClass"
                        />
                      </span>
                      <span>{{ loc.label }}</span>
                    </span>
                  </SSelectItem>
                </SSelectContent>
              </SSelect>
              <p class="text-xs text-muted-foreground">
                {{ t("cashfloat.v2.from-helper") }}
              </p>
            </div>

            <div class="grid grid-cols-2 gap-2">
              <SButton
                v-if="selectedPaymentMethod === PaymentMethods.METHOD_POS_CASH"
                type="button"
                variant="outline"
                size="sm"
                :class="
                  !isMatch(currency)
                    ? '!bg-sky-50 !border-sky-300 !text-sky-700 hover:!bg-sky-100'
                    : ''
                "
                :data-cy="`v2-close-view-details-${currency}`"
                @click.stop="selectedCurrency = currency"
              >
                <LayoutGridIcon class="w-3 h-3 mr-2" />
                {{ t("cashfloat.v2.close.view-details") }}
              </SButton>
              <SButton
                type="button"
                variant="outline"
                size="sm"
                :class="
                  !isMatch(currency)
                    ? '!bg-orange-50 !border-orange-300 !text-orange-700 hover:!bg-orange-100'
                    : ''
                "
                :data-cy="`v2-close-add-note-${currency}`"
                @click.stop="openNoteDialog(currency)"
              >
                <NotebookPenIcon class="w-3 h-3 mr-2" />
                {{
                  noteFor(currency)
                    ? t("cashfloat.v2.close.edit-note")
                    : t("cashfloat.add-note")
                }}
              </SButton>
            </div>
          </SCard>
        </div>
      </div>

      <!-- Automatic reconciliation: summary table -->
      <div
        v-if="activeTab === 'auto'"
        class="px-8 mb-4"
        data-cy="v2-close-auto-summary"
      >
        <SCard class="!gap-0 !py-0 px-0">
          <div
            class="flex items-center gap-2 px-6 py-4 border-b border-slate-100"
          >
            <LayoutGridIcon class="w-4 h-4 text-[#044c55]" />
            <p class="font-semibold">
              {{ t("cashfloat.digital-methods-summary") }}
            </p>
          </div>
          <div
            class="grid grid-cols-[1.5fr_1fr_1fr_1fr] items-center px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-slate-50 border-b border-slate-200"
          >
            <div>{{ t("cashfloat.payment-methods") }}</div>
            <div class="text-right">
              {{ t("cashfloat.payments") }}
            </div>
            <div class="text-right">
              {{ t("cashfloat.refunds") }}
            </div>
            <div class="text-right">
              {{ t("cashfloat.tips") }}
            </div>
          </div>
          <div class="divide-y divide-slate-100 text-sm">
            <div
              v-for="row in autoSummaryRows"
              :key="row.key"
              class="grid grid-cols-[1.5fr_1fr_1fr_1fr] items-center px-6 py-3"
              :data-cy="`v2-close-auto-row-${row.key}`"
            >
              <div>{{ row.label }}</div>
              <div class="text-right">
                {{ formatMoney(row.payment, autoCurrency) }}
              </div>
              <div class="text-right">
                {{ formatMoney(row.refund, autoCurrency) }}
              </div>
              <div
                class="text-right"
                :data-cy="`v2-close-auto-row-${row.key}-tip`"
              >
                {{ formatMoney(row.tip, autoCurrency) }}
              </div>
            </div>
            <div
              class="grid grid-cols-[1.5fr_1fr_1fr_1fr] items-center px-6 py-3 font-semibold text-[#044c55]"
              data-cy="v2-close-auto-total"
            >
              <div>{{ t("cashfloat.v2.close.total") }}</div>
              <div class="text-right">
                {{ formatMoney(autoTotal.payment, autoCurrency) }}
              </div>
              <div class="text-right">
                {{ formatMoney(autoTotal.refund, autoCurrency) }}
              </div>
              <div class="text-right" data-cy="v2-close-auto-total-tip">
                {{ formatMoney(autoTotal.tip, autoCurrency) }}
              </div>
            </div>
          </div>
        </SCard>
      </div>

      <!-- Footer -->
      <div
        class="sticky bottom-0 left-0 right-0 z-10 bg-white border-t border-slate-200 px-8 py-4 mt-8 flex items-center justify-between gap-4"
      >
        <div
          v-if="hasUnjustifiedMismatch"
          class="flex items-start gap-2 text-sm text-red-700 max-w-lg"
          data-cy="v2-close-footer-warning-mismatch"
        >
          <TriangleAlertIcon class="w-4 h-4 mt-0.5 shrink-0 text-red-600" />
          <p>
            {{ t("cashfloat.v2.note-explain") }}
          </p>
        </div>
        <div
          v-else
          class="flex items-start gap-2 text-sm text-amber-900 max-w-lg"
          data-cy="v2-close-footer-warning"
        >
          <TriangleAlertIcon class="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
          <p>
            {{ t("cashfloat.v2.close.footer-warning") }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <SButton
            type="button"
            variant="outline"
            size="lg"
            :disabled="isSubmitting"
            data-cy="v2-close-cancel"
            @click="cancelAndReturn"
          >
            <ArrowLeftIcon class="w-4 h-4 mr-2" />
            {{ t("cashfloat.cancel-return") }}
          </SButton>
          <SButton
            type="button"
            size="lg"
            :loading="isSubmitting"
            :disabled="hasUnjustifiedMismatch"
            data-cy="v2-close-accept"
            @click="openConfirmCloseModal"
          >
            <CheckIcon class="w-4 h-4 mr-2" />
            {{ t("cashfloat.v2.close.accept-and-close-sale") }}
          </SButton>
        </div>
      </div>

      <!-- Confirm closure modal — extracted component -->
      <CloseConfirmModalV2
        v-model:open="showConfirmClose"
        :location-options="locationOptions"
        :is-submitting="isSubmitting"
        :has-unsynced-orders="hasUnsyncedOrders"
        :locked-location-id="syncedClosureTransfers?.locationId ?? null"
        @confirm="acceptAndClose"
        @add-transfer="goToAddTransfer"
      />

      <!-- Note dialog (plain overlay to avoid el-overlay SCSS conflict) -->
      <div
        v-if="showNoteDialog"
        class="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40"
        data-cy="v2-close-note-dialog"
      >
        <div
          class="bg-white rounded-lg shadow-xl w-full max-w-md p-2 flex flex-col gap-4"
        >
          <p class="text-lg font-semibold">
            {{ t("cashfloat.v2.close.note-for", { currency: noteCurrency }) }}
          </p>
          <STextarea
            v-model="noteDraft"
            :rows="4"
            :placeholder="t('cashfloat.v2.add-note-placeholder')"
            data-cy="v2-close-note-input"
          />
          <div class="flex items-center justify-end gap-2">
            <SButton
              variant="outline"
              size="sm"
              @click="showNoteDialog = false"
            >
              {{ $t("button.cancel") }}
            </SButton>
            <SButton size="sm" :disabled="!noteDraft.trim()" @click="saveNote">
              {{ $t("button.save") }}
            </SButton>
          </div>
        </div>
      </div>

      <!-- Cash inventory modal (plain overlay to avoid el-overlay SCSS conflict) -->
      <div
        v-if="showCashInventoryDialog"
        class="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40"
        data-cy="v2-close-cash-inventory-dialog"
      >
        <div
          class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 flex flex-col gap-4"
          data-cy="v2-inventory-panel"
        >
          <p class="text-lg font-semibold">
            {{
              t("payment-method-in-currency", {
                method: t("payment-method.CASH"),
                currency: inventoryCurrency,
              })
            }}
          </p>
          <SCard
            class="!gap-0 !py-0 overflow-hidden shrink-0"
            data-cy="v2-inventory-card"
          >
            <div
              class="grid grid-cols-[1fr_auto_1fr_1fr] items-center px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-slate-50 border-b border-slate-200"
            >
              <div>{{ t("cashfloat.v2.denomination") }}</div>
              <div class="text-center px-4">{{ t("field.quantity") }}</div>
              <div>{{ t("cashfloat.v2.unit-value") }}</div>
              <div class="text-right">{{ t("dashboards.subtotal") }}</div>
            </div>
            <div
              v-for="(entry, idx) in inventoryEntries()"
              :key="`${entry.kind}-${entry.name}`"
              class="grid grid-cols-[1fr_auto_1fr_1fr] items-center px-4 py-3 text-sm"
              :class="
                idx < inventoryEntries().length - 1
                  ? 'border-b border-slate-100'
                  : ''
              "
            >
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex items-center justify-center w-7 h-7 rounded-md shrink-0"
                  :class="
                    entry.kind === 'bill'
                      ? 'bg-slate-100 text-slate-600'
                      : 'bg-amber-50 text-amber-600'
                  "
                >
                  <BanknoteIcon v-if="entry.kind === 'bill'" class="w-4 h-4" />
                  <CoinsIcon v-else class="w-4 h-4" />
                </span>
                <span>
                  {{
                    entry.kind === "bill"
                      ? t("cashfloat.bills")
                      : t("cashfloat.coins")
                  }}
                  · {{ entry.displayName }}
                </span>
              </div>
              <div class="flex items-center gap-2 px-4">
                <button
                  type="button"
                  class="w-7 h-7 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center justify-center disabled:opacity-40"
                  :disabled="entry.userInput <= 0"
                  :data-cy="`v2-inventory-dec-${entry.kind}-${entry.name}`"
                  @click="
                    updateInventoryDenomination(
                      entry.kind,
                      entry.name,
                      entry.userInput - 1
                    )
                  "
                >
                  <MinusIcon class="w-3 h-3" />
                </button>
                <span
                  class="min-w-[2rem] text-center font-semibold"
                  :data-cy="`v2-inventory-qty-${entry.kind}-${entry.name}`"
                >
                  {{ entry.userInput }}
                </span>
                <button
                  type="button"
                  class="w-7 h-7 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center justify-center"
                  :data-cy="`v2-inventory-inc-${entry.kind}-${entry.name}`"
                  @click="
                    updateInventoryDenomination(
                      entry.kind,
                      entry.name,
                      entry.userInput + 1
                    )
                  "
                >
                  <PlusIcon class="w-3 h-3" />
                </button>
              </div>
              <div class="text-muted-foreground">
                {{ inventoryCurrency }}
                {{ formatAmount(entry.unitValue) }}
              </div>
              <div class="text-right font-medium">
                {{ inventoryCurrency }}
                {{ formatAmount(entry.unitValue * entry.userInput) }}
              </div>
            </div>
            <div
              class="grid grid-cols-[1fr_auto_1fr_1fr] items-center px-4 py-3 bg-slate-50 border-t border-slate-200 font-semibold"
            >
              <div class="col-span-3">{{ t("field.total") }}</div>
              <div class="text-right" data-cy="v2-inventory-total">
                {{ inventoryCurrency }}
                {{ formatAmount(inventoryTotalMajor()) }}
              </div>
            </div>
          </SCard>
          <div class="flex items-center justify-end gap-2">
            <SButton
              variant="outline"
              size="sm"
              data-cy="v2-inventory-cancel"
              @click="cancelCashInventory"
            >
              {{ $t("button.cancel") }}
            </SButton>
            <SButton
              size="sm"
              data-cy="v2-inventory-confirm"
              @click="confirmCashInventory"
            >
              {{ $t("button.confirm") }}
            </SButton>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, h, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  SButton,
  SCard,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
  SInputGroupText,
  SSelect,
  SSelectContent,
  SSelectItem,
  SSelectTrigger,
  SSelectValue,
  STextarea,
} from "@smeetz/ui-core";
import {
  ArrowLeftIcon,
  ArrowLeftRightIcon,
  BanknoteIcon,
  CheckIcon,
  CoinsIcon,
  InfoIcon,
  LayoutGridIcon,
  MinusIcon,
  NotebookPenIcon,
  PlusIcon,
  TriangleAlertIcon,
} from "@smeetz/ui-icons";

import CashFloatModule from "@/store/modules/cash-float";
import POSConfigModule from "@/store/modules/pos-config";
import ModalModule from "@/store/modules/modal";
import DeviceModule from "@/store/modules/device";
import { CashLocationDeviceType } from "@/constants/cash-location";
import { clearOrders } from "@/utils/vue-helpers/orders.helper";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { clearReflectedSessions } from "@/utils/vue-helpers/order-stock.helper";
import { unsyncedOrdersCount } from "@/db/orders";
import { unsyncedPaymentsCount } from "@/db/payments";
import {
  initiateOrderSync,
  initiatePaymentSync,
} from "@/services/sync-service";
import Timeout from "await-timeout";
import { ModalStatus } from "@/constants/modal";
import { LogLevel, LogService } from "@/services/logging";
import NotificationService from "@/services/notification.service";
import {
  getManualClosingSummaryV2 as getManualClosingSummary,
  getDigitalClosingSummaryV2 as getDigitalClosingSummary,
  pushSyncCashTransferV2,
  pushSyncCashfloat,
} from "@/api/cash-float/cash-float";
import { clearDexieDB } from "@/utils/vue-helpers/db";
import { handleCashTransferSync } from "@/utils/vue-helpers/cash-float";
import { locationVisualFor } from "@/utils/vue-helpers/cash-location";
import { generateID } from "@/utils/helpers/app";
import {
  mapCashLocation,
  processMoneyInOutEvent,
} from "@/utils/helpers/compliance";
import { MoneyInOutType } from "@smeetz/compliance-common";
import { PaymentMethod } from "@smeetz/order-common";
import dayjs from "dayjs";
import type {
  ICashFloat,
  ICashTransfer,
  IManualClosingSummary,
  IDigitalClosingSummary,
  IPaymentMethodSummary,
} from "@/interfaces/cash-float/cash-float";
import { Currencies, allDetailedCurrencies } from "@/constants/currency";
import { PaymentMethods } from "@/types/db/accounting";
import { formatDateTime } from "@/utils/helpers/dates";
import { openCashDrawer as openCashDrawerUtil } from "@/utils/helpers/cross-platform";
import UserModule from "@/store/modules/user";
import { getGroupInfo } from "@/utils/vue-helpers/group";
import { printClosingSummaryReceipt } from "@/printing/handlers/receipts";
import type {
  IClosingSummaryReceipt,
  IPaymentMethodSection,
  IReceiptLine,
  IReceiptSection,
} from "@/interfaces/receipts";

const { t } = useI18n({});

// ---------- Connectivity ----------
// Cash transfers sync to the backend, so the "Add a cash transfer" action is
// disabled while offline. Tracked reactively via the browser online events.
const isOnline = ref(
  typeof navigator !== "undefined" ? navigator.onLine : true
);
function handleOnline() {
  isOnline.value = true;
}
function handleOffline() {
  isOnline.value = false;
}
onMounted(() => {
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
});
onBeforeUnmount(() => {
  window.removeEventListener("online", handleOnline);
  window.removeEventListener("offline", handleOffline);
});

// ---------- State ----------
const activeTab = ref<"manual" | "auto">("manual");
const isLoading = ref(true);
const isSubmitting = ref(false);
const manualSummary = ref<IManualClosingSummary | null>(null);
const digitalSummary = ref<IDigitalClosingSummary | null>(null);
// Tracks the store live — a one-time snapshot would go stale if
// CashFloatModule.cashFloat gets hydrated asynchronously after this
// component mounts (e.g. via the restore() mutation).
const cashfloat = computed<ICashFloat | null>(() => CashFloatModule.cashFloat);

const activeCurrencies = ref<string[]>([]);
const selectedCurrency = ref<string>("");
const selectedPaymentMethod = ref<string>(PaymentMethods.METHOD_POS_CASH);
// Keyed by "<paymentMethod>:<currency>" — cash and other manual methods can
// both use the group currency, so a bare currency key would collide.
const countedInput = ref<Record<string, string>>({});
const focusedCurrency = ref<string>("");

// Note dialog
const showNoteDialog = ref(false);
const noteDraft = ref("");
const noteCurrency = ref<string>("");

// ---------- Computed ----------
const groupCurrency = computed<Currencies>(() => {
  const c =
    POSConfigModule.groupCurrencies?.find((x: any) => x.isDefault === 1)
      ?.label || Currencies.CHF;
  return c as Currencies;
});

const cashPaymentSummary = computed<IPaymentMethodSummary | null>(() => {
  return (
    manualSummary.value?.amountsPerPaymentMethods.find(
      (pm) => pm.name === PaymentMethods.METHOD_POS_CASH
    ) || null
  );
});

// Manual tab shows every payment method the backend returns (cash, cheque,
// bank transfer, ...), matching V1 — not just cash. Cash rounding is the one
// exception: it is a derived adjustment rather than a tender the operator
// counts, so it gets no row of its own. It still counts towards the totals.
const paymentMethods = computed<IPaymentMethodSummary[]>(
  () =>
    manualSummary.value?.amountsPerPaymentMethods.filter(
      (pm) => pm.name !== PaymentMethods.METHOD_CASH_ROUNDING
    ) || []
);

const selectedPaymentSummary = computed<IPaymentMethodSummary | null>(() => {
  return (
    paymentMethods.value.find(
      (pm) => pm.name === selectedPaymentMethod.value
    ) || null
  );
});

// Cash tracks every active currency; other manual methods (cheque, bank
// transfer, ...) only track the group's default currency, matching V1.
const currenciesForSelectedMethod = computed<string[]>(() =>
  selectedPaymentMethod.value === PaymentMethods.METHOD_POS_CASH
    ? activeCurrencies.value
    : [groupCurrency.value]
);

function countedKey(paymentMethod: string, currency: string): string {
  return `${paymentMethod}:${currency}`;
}

function selectPaymentMethod(name: string) {
  selectedPaymentMethod.value = name;
  const currencies =
    name === PaymentMethods.METHOD_POS_CASH
      ? activeCurrencies.value
      : [groupCurrency.value];
  if (!currencies.includes(selectedCurrency.value)) {
    selectedCurrency.value = currencies[0] || "";
  }
}

const openedByName = computed(() => {
  if (manualSummary.value?.openedBy) {
    const { firstName, lastName } = manualSummary.value.openedBy;
    return `${firstName} ${lastName}`.trim();
  }
  return "";
});

const sessionStartDate = computed(() => {
  if (manualSummary.value?.createdAt) {
    return formatDateTime(manualSummary.value.createdAt);
  }
  return formatDateTime(cashfloat.value?.createdAt || "");
});

// True while either sync queue (orders, payments) still holds rows. Shown as
// a warning on the summary and the confirm modal, and re-checked as a hard
// gate in acceptAndClose before the local data is cleared.
const hasUnsyncedOrders = ref(false);
const summaryMissesQueuedRows = ref(false);
const editedCountKeys = new Set<string>();
async function refreshUnsyncedOrders(): Promise<boolean> {
  hasUnsyncedOrders.value =
    (await unsyncedOrdersCount({ includeInProgress: true })) +
      (await unsyncedPaymentsCount()) >
    0;
  return hasUnsyncedOrders.value;
}

async function drainSyncQueues(): Promise<boolean> {
  try {
    for (let round = 0; round < 5; round++) {
      if (!(await refreshUnsyncedOrders())) return true;
      if (!navigator.onLine) return false;
      await initiateOrderSync();
      await initiatePaymentSync();
      if (!(await refreshUnsyncedOrders())) return true;
      await Timeout.set(2000);
    }
  } catch (error) {
    LogService.info(
      "V2 close: error draining the sync queues",
      { error },
      LogLevel.Warning
    );
  }
  return false;
}

// ---------- Helpers ----------
function formatMoney(amountInCents: number, currency: string): string {
  const major = (amountInCents / 100).toFixed(2);
  return `${currency} ${major}`;
}

function getInitialAmount(pm: IPaymentMethodSummary, currency: string): number {
  if (pm.name !== PaymentMethods.METHOD_POS_CASH) return 0;
  return pm.startAmounts?.[currency] || 0;
}

function getExpectedAmount(
  pm: IPaymentMethodSummary,
  currency: string
): number {
  return pm.amounts?.[currency] || 0;
}

function getCountedAmount(pm: IPaymentMethodSummary, currency: string): number {
  return pm.countedAmounts?.[currency] || 0;
}

function getDifference(pm: IPaymentMethodSummary, currency: string): number {
  return getCountedAmount(pm, currency) - getExpectedAmount(pm, currency);
}

function getCustomerAmountIn(
  pm: IPaymentMethodSummary,
  currency: string
): number {
  return pm.cutomerAmountIn?.[currency] || 0;
}

function getRefund(pm: IPaymentMethodSummary, currency: string): number {
  return pm.refund?.[currency] || 0;
}

function getChange(pm: IPaymentMethodSummary, currency: string): number {
  if (pm.name !== PaymentMethods.METHOD_POS_CASH) return 0;
  return pm.change?.[currency] || 0;
}

function getSettlement(pm: IPaymentMethodSummary, currency: string): number {
  return (
    getCustomerAmountIn(pm, currency) +
    getRefund(pm, currency) +
    getChange(pm, currency)
  );
}

function getTransferIn(pm: IPaymentMethodSummary, currency: string): number {
  if (pm.name !== PaymentMethods.METHOD_POS_CASH) return 0;
  return pm.transferIn?.[currency] || 0;
}

function getTransferOut(pm: IPaymentMethodSummary, currency: string): number {
  if (pm.name !== PaymentMethods.METHOD_POS_CASH) return 0;
  return pm.transferOut?.[currency] || 0;
}

function getNote(pm: IPaymentMethodSummary, currency: string): string {
  return pm.notes?.[currency] || "";
}

function differenceLabel(pm: IPaymentMethodSummary, currency: string): string {
  const diff = getDifference(pm, currency);
  if (diff === 0) return `${currency} 0.00`;
  const sign = diff > 0 ? "+" : "−";
  const major = (Math.abs(diff) / 100).toFixed(2);
  return `${sign}${currency} ${major}`;
}

function isMatch(currency: string): boolean {
  if (!selectedPaymentSummary.value) return true;
  return getDifference(selectedPaymentSummary.value, currency) === 0;
}

// ---------- Automatic reconciliation ----------
// Digital summary covers card/voucher/giftcard via dedicated buckets; bank
// transfers are tracked in the manual summary as a payment method, so we
// reach for it there.
const autoCurrency = computed<string>(() => groupCurrency.value);

const bankTransferSummary = computed(() => {
  const pm = manualSummary.value?.amountsPerPaymentMethods.find(
    (p) =>
      p.name === PaymentMethods.METHODS_POS_BANK_TRANSFER ||
      p.name === PaymentMethods.METHOD_BANK_TRANSFER
  );
  if (!pm) return { payment: 0, refund: 0 };
  const c = autoCurrency.value;
  return {
    payment: pm.cutomerAmountIn?.[c] || 0,
    refund: pm.refund?.[c] || 0,
  };
});

const autoSummaryRows = computed(() => {
  const ds = digitalSummary.value;
  const rows = [
    {
      key: "credit-card",
      label: t("cashfloat.credit_card"),
      payment: ds?.totalCreditCard.payment || 0,
      refund: ds?.totalCreditCard.refund || 0,
      tip: ds?.totalCreditCard.tip || 0,
    },
    {
      key: "vouchers",
      label: t("cashfloat.vouchers"),
      payment: ds?.totalVoucher.payment || 0,
      refund: ds?.totalVoucher.refund || 0,
      tip: ds?.totalVoucher.tip || 0,
    },
    {
      key: "gift-cards",
      label: t("cashfloat.gift-cards"),
      payment: ds?.totalGiftCard.payment || 0,
      refund: ds?.totalGiftCard.refund || 0,
      tip: ds?.totalGiftCard.tip || 0,
    },
    {
      key: "bank-transfer",
      label: t("cashfloat.v2.close.bank-transfer"),
      payment: bankTransferSummary.value.payment,
      refund: bankTransferSummary.value.refund,
      tip: 0,
    },
  ];
  return rows;
});

const autoTotal = computed(() => {
  return autoSummaryRows.value.reduce(
    (acc, r) => ({
      payment: acc.payment + r.payment,
      refund: acc.refund + r.refund,
      tip: acc.tip + r.tip,
    }),
    { payment: 0, refund: 0, tip: 0 }
  );
});

// Counterparty picked per mismatched currency — the source of a surplus or
// the destination of a deficit for the difference transfer (FIS-553).
const differenceLocationByCurrency = ref<Record<string, number | null>>({});

// True while any currency has a counted ≠ expected gap that the cashier
// hasn't justified with a note and (on linked devices) a counterparty
// location. Drives the Accept button's disabled state.
const hasUnjustifiedMismatch = computed(() => {
  const pm = cashPaymentSummary.value;
  if (!pm) return false;
  for (const c of activeCurrencies.value) {
    if (getDifference(pm, c) === 0) continue;
    if (!getNote(pm, c).trim()) {
      return true;
    }
    if (hardwareLocId.value != null && !differenceLocationByCurrency.value[c]) {
      return true;
    }
  }
  return false;
});

// ---------- Confirm-closure modal ----------
const showConfirmClose = ref(false);

// FIS-752: destination transfers this closure already synced on a failed close
const syncedClosureTransfers = computed(() => {
  const synced = CashFloatModule.closureTransfersSynced;
  return synced && synced.cashfloatUid === cashfloat.value?.uid ? synced : null;
});

// Manual payment methods only (digital methods are settled separately).
const MANUAL_PAYMENT_METHODS = new Set<string>([
  PaymentMethods.METHOD_POS_CASH,
  PaymentMethods.METHODS_POS_CHEQUE,
  PaymentMethods.METHODS_POS_BANK_TRANSFER,
]);

const paymentMethodLabel: Record<string, string> = {
  [PaymentMethods.METHOD_POS_CASH]: "Cash",
  [PaymentMethods.METHODS_POS_CHEQUE]: "Check",
  [PaymentMethods.METHODS_POS_BANK_TRANSFER]: "Bank transfer",
};

const confirmCloseRows = computed(() => {
  const rows: {
    method: string;
    label: string;
    currency: string;
    amount: number;
  }[] = [];
  for (const pm of manualSummary.value?.amountsPerPaymentMethods || []) {
    if (!MANUAL_PAYMENT_METHODS.has(pm.name)) continue;
    for (const c of activeCurrencies.value) {
      const counted = pm.countedAmounts?.[c] || 0;
      if (counted <= 0) continue;
      rows.push({
        method: pm.name,
        label: paymentMethodLabel[pm.name] || pm.name,
        currency: c,
        amount: counted,
      });
    }
  }
  return rows;
});

const totalDrawerInGroupCurrency = computed(() => {
  let total = 0;
  for (const row of confirmCloseRows.value) {
    const c = POSConfigModule.groupCurrencies?.find(
      (cur: any) => cur.label === row.currency
    );
    const rate = c?.exchangeRate || 1;
    total += row.amount * rate;
  }
  return total;
});

const hardwareLocId = computed(() =>
  DeviceModule.hardwareLocation
    ? Number(DeviceModule.hardwareLocation.locationId)
    : null
);

// Whether this machine is linked to a cash location — gates the device line
// and transfer in/out rows on the closing receipt, matching V1.
const hasLocation = computed(() => !!DeviceModule.hardwareLocation?.locationId);

// Destinations come from every cash location of the group — a location
// exists even when no device is attached to it (e.g. a safe), which a
// device-based list would miss. Canceled locations, kiosks and this
// device's own location stay excluded.
const locationOptions = computed(() =>
  (DeviceModule.locations || [])
    .filter(
      (l: any) =>
        !l.canceled &&
        Number(l.id) !== hardwareLocId.value &&
        l.deviceType !== CashLocationDeviceType.KIOSK
    )
    .map((l: any) => ({
      value: Number(l.id),
      label: String(l.name ?? ""),
      deviceType: l.deviceType ?? null,
    }))
);

// Same options dressed with the shared per-type icon/colors, for the
// difference counterparty picker in the currency cards.
const richLocationOptions = computed(() =>
  locationOptions.value.map((l) => ({
    ...l,
    ...locationVisualFor(l.deviceType),
  }))
);

function selectedDifferenceLocation(currency: string) {
  return richLocationOptions.value.find(
    (l) => l.value === differenceLocationByCurrency.value[currency]
  );
}

function openConfirmCloseModal() {
  // Block if any unjustified mismatch — the Accept button is also disabled
  // for safety, but double-check here.
  if (hasUnjustifiedMismatch.value) return;
  // Refresh the warning; the banner updates reactively once the counts load.
  void refreshUnsyncedOrders();
  showConfirmClose.value = true;
}

function goToAddTransfer() {
  showConfirmClose.value = false;
  ModalModule.setStatus(ModalStatus.CashInOut);
}

// ---------- Actions ----------
function onCountedFocus(currency: string) {
  focusedCurrency.value = currency;
}

function onCountedBlur(currency: string) {
  commitCounted(currency);
  if (focusedCurrency.value === currency) focusedCurrency.value = "";
}

function onCountedInput(currency: string) {
  const pm = selectedPaymentSummary.value;
  if (pm) editedCountKeys.add(countedKey(pm.name, currency));
  commitCounted(currency);
}

function commitCounted(currency: string) {
  const pm = selectedPaymentSummary.value;
  if (!pm) return;
  if (!pm.countedAmounts) pm.countedAmounts = {};
  const key = countedKey(pm.name, currency);
  const major = Number(countedInput.value[key] || 0);
  pm.countedAmounts[currency] = Math.round(major * 100);
}

// ---------- Cash inventory (bills/coins breakdown) ----------
type DenominationLine = { name: number; userInput: number };
interface InventoryState {
  bills: DenominationLine[];
  coins: DenominationLine[];
  cents: DenominationLine[];
}
type DenominationEntry = {
  kind: "bill" | "coin" | "cent";
  name: number;
  displayName: string;
  unitValue: number;
  userInput: number;
};

const showCashInventoryDialog = ref(false);
const inventoryCurrency = ref<string>("");
// Preserve entered counts per currency across dialog opens within the session.
const inventoryByCurrency = ref<Record<string, InventoryState>>({});

function inventoryEntries(): DenominationEntry[] {
  const state = inventoryByCurrency.value[inventoryCurrency.value];
  if (!state) return [];
  const billLines: DenominationEntry[] = state.bills.map((b) => ({
    kind: "bill",
    name: b.name,
    displayName: String(b.name),
    unitValue: b.name,
    userInput: b.userInput,
  }));
  const coinLines: DenominationEntry[] = state.coins.map((c) => ({
    kind: "coin",
    name: c.name,
    displayName: String(c.name),
    unitValue: c.name,
    userInput: c.userInput,
  }));
  const centLines: DenominationEntry[] = state.cents.map((c) => ({
    kind: "cent",
    name: c.name,
    displayName: (c.name / 100).toFixed(2),
    unitValue: c.name / 100,
    userInput: c.userInput,
  }));
  return [...billLines, ...coinLines, ...centLines];
}

function inventoryTotalMajor(): number {
  const state = inventoryByCurrency.value[inventoryCurrency.value];
  if (!state) return 0;
  let sum = 0;
  for (const b of state.bills) sum += b.name * b.userInput;
  for (const c of state.coins) sum += c.name * c.userInput;
  for (const c of state.cents) sum += (c.name * c.userInput) / 100;
  return sum;
}

function updateInventoryDenomination(
  kind: "bill" | "coin" | "cent",
  name: number,
  next: number
) {
  const state = inventoryByCurrency.value[inventoryCurrency.value];
  if (!state) return;
  const newValue = Math.max(0, Math.floor(next));
  const bucket =
    kind === "bill" ? state.bills : kind === "coin" ? state.coins : state.cents;
  const line = bucket.find((d) => d.name === name);
  if (!line) return;
  line.userInput = newValue;
}

function formatAmount(value: number): string {
  return value
    .toLocaleString("en-EN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .split(",")
    .join("'");
}

function openCashInventory(currency: string) {
  selectedCurrency.value = currency;
  inventoryCurrency.value = currency;
  if (!inventoryByCurrency.value[currency]) {
    const template = allDetailedCurrencies.find((c) => c.label === currency);
    inventoryByCurrency.value[currency] = {
      bills: (template?.bills || []).map((b) => ({
        name: b.name,
        userInput: 0,
      })),
      coins: (template?.coins || []).map((c) => ({
        name: c.name,
        userInput: 0,
      })),
      cents: (template?.cents || []).map((c) => ({
        name: c.name,
        userInput: 0,
      })),
    };
  }
  showCashInventoryDialog.value = true;
}

function confirmCashInventory() {
  const pm = cashPaymentSummary.value;
  const currency = inventoryCurrency.value;
  if (!pm || !currency) return;
  if (!pm.countedAmounts) pm.countedAmounts = {};
  const cents = Math.round(inventoryTotalMajor() * 100);
  pm.countedAmounts[currency] = cents;
  countedInput.value[countedKey(pm.name, currency)] = (cents / 100).toFixed(2);
  editedCountKeys.add(countedKey(pm.name, currency));
  showCashInventoryDialog.value = false;
}

function cancelCashInventory() {
  showCashInventoryDialog.value = false;
}

function openNoteDialog(currency: string) {
  noteCurrency.value = currency;
  const pm = selectedPaymentSummary.value;
  noteDraft.value = pm ? getNote(pm, currency) : "";
  showNoteDialog.value = true;
}

function saveNote() {
  const trimmed = noteDraft.value.trim();
  if (!trimmed) {
    // Empty notes aren't accepted — the cashier must explain the mismatch.
    return;
  }
  const pm = selectedPaymentSummary.value;
  if (pm) {
    if (!pm.notes) pm.notes = {};
    pm.notes[noteCurrency.value] = trimmed;
  }
  showNoteDialog.value = false;
}

function noteFor(currency: string): string {
  const pm = selectedPaymentSummary.value;
  return pm ? getNote(pm, currency) : "";
}

function addCashTransfer() {
  ModalModule.setStatus(ModalStatus.CashInOut);
}

function openCashDrawer() {
  try {
    openCashDrawerUtil();
  } catch (error) {
    LogService.info("V2 close: error opening cash drawer", { error });
  }
}

function cancelAndReturn() {
  CashFloatModule.setShowClosingSummary(false);
  CashFloatModule.setIsClosingCashfloat(false);
}

async function acceptAndClose(payload?: {
  locationId: number | null;
  staysInDrawer: boolean;
}) {
  showConfirmClose.value = false;
  // Block submit if any currency has a difference without a note, or (on
  // linked devices) without a counterparty for the difference transfer.
  const pm = cashPaymentSummary.value;
  if (pm) {
    for (const c of activeCurrencies.value) {
      if (getDifference(pm, c) === 0) continue;
      if (!getNote(pm, c)) {
        NotificationService.showCustomMessage(
          t("cashfloat.note-required", { currency: c }),
          "warning"
        );
        return;
      }
      if (
        hardwareLocId.value != null &&
        !differenceLocationByCurrency.value[c]
      ) {
        NotificationService.showCustomMessage(
          t("cashfloat.v2.justify-difference-warning", { currencies: c }),
          "warning"
        );
        return;
      }
    }
  }
  if (!cashfloat.value || !cashfloat.value.uid || !manualSummary.value) {
    LogService.info("V2 close: missing cashfloat or summary, aborting");
    NotificationService.showCustomMessage(t("error.close-cashfloat"), "error");
    return;
  }

  isSubmitting.value = true;
  try {
    LogService.info(
      `V2 close: submit triggered for cashfloat uid=${cashfloat.value.uid}`
    );

    // Closing wipes the local DB, so never close while orders or payments are still queued.
    if (summaryMissesQueuedRows.value || (await refreshUnsyncedOrders())) {
      const drained = await drainSyncQueues();
      LogService.info(
        `V2 close: queued orders/payments at submit, drained=${drained}`,
        {},
        LogLevel.Warning
      );
      if (!drained) {
        NotificationService.showCustomMessage(
          t("cashfloat.v2.close.orders-not-synced-close-blocked"),
          "warning"
        );
        return;
      }
      await Promise.all([
        fetchManualSummary(manualSummary.value),
        fetchDigitalSummary(),
      ]);
      summaryMissesQueuedRows.value = false;
      NotificationService.showCustomMessage(
        t("cashfloat.v2.close.summary-updated-after-sync"),
        "warning"
      );
      return;
    }

    // Cash already sent on a failed close must still match the count
    const alreadySynced = syncedClosureTransfers.value;
    const changedKey = Object.keys(alreadySynced?.amounts ?? {}).find((key) => {
      const row = confirmCloseRows.value.find(
        (r) => `${r.method}:${r.currency}` === key
      );
      return (row?.amount ?? 0) !== alreadySynced?.amounts[key];
    });
    if (alreadySynced && changedKey) {
      LogService.info(
        `V2 close: count changed after the destination transfer synced for ${changedKey}, cashfloat uid=${cashfloat.value.uid}`,
        { synced: alreadySynced, rows: confirmCloseRows.value }
      );
      NotificationService.showCustomMessage(
        t("error.close-cashfloat-count-changed"),
        "error"
      );
      return;
    }

    // Counted ≠ expected reaches the drawer ledger as a normal cash transfer
    // with both ends set (FIS-553): a surplus flows in from the selected
    // counterparty, a deficit flows out to it. Guarded by initialTransferDone
    // so a retried submit can't duplicate it, plus per-currency tracking so
    // a retry after a partial failure only re-fires the currencies whose
    // transfer never synced (FIS-508).
    const cashPm = cashPaymentSummary.value;
    if (
      hardwareLocId.value != null &&
      cashPm &&
      !CashFloatModule.initialTransferDone
    ) {
      const cfUid = cashfloat.value.uid;
      const alreadySyncedCurrencies =
        CashFloatModule.gainLossSyncedCurrencies?.cashfloatUid === cfUid
          ? CashFloatModule.gainLossSyncedCurrencies.currencies
          : [];
      // FIS-508: only arm the retry guard when a difference transfer actually
      // synced for this closure. A zero-difference close must not set the
      // flag — if that close then fails and selling resumes, a later close
      // with a real difference would silently skip its transfer.
      let differenceTransferFired = alreadySyncedCurrencies.length > 0;
      for (const currency of activeCurrencies.value) {
        const difference = getDifference(cashPm, currency);
        if (difference === 0) continue;
        if (alreadySyncedCurrencies.includes(currency)) {
          LogService.info(
            `V2 close: skipping difference transfer for ${currency}, already synced on a previous submit of cashfloat uid=${cfUid}`
          );
          continue;
        }
        const counterParty = differenceLocationByCurrency.value[currency];
        if (!counterParty) {
          // Gated by hasUnjustifiedMismatch and the pre-submit check above;
          // never sync a one-ended transfer if state got out of step.
          NotificationService.showCustomMessage(
            t("cashfloat.v2.justify-difference-warning", {
              currencies: currency,
            }),
            "warning"
          );
          return;
        }
        const isGain = difference > 0;
        const amount = Math.abs(difference / 100);
        const differenceTransfer: ICashTransfer = {
          _id: dayjs().toISOString(),
          createdAt: dayjs().toISOString(),
          amount,
          currency: currency as Currencies,
          groupId: Number(POSConfigModule.groupId),
          from: isGain ? counterParty : hardwareLocId.value,
          to: isGain ? hardwareLocId.value : counterParty,
          note: getNote(cashPm, currency) || null,
          channel: "Smeetz POS",
          uuid: generateID(),
          cfUid,
        };
        LogService.info(
          `V2 close: adding difference transfer for ${currency}, cashfloat uid=${cashfloat.value.uid}`,
          { cashTransfer: differenceTransfer }
        );
        const isSynced = await handleCashTransferSync(differenceTransfer);
        if (!isSynced) {
          return;
        }
        // Record right after the sync: a retried submit must never re-fire
        // this currency's transfer, even if the compliance event below fails.
        CashFloatModule.addGainLossSyncedCurrency({
          cashfloatUid: cfUid,
          currency,
        });
        differenceTransferFired = true;
        try {
          await processMoneyInOutEvent({
            moneyInOutType: MoneyInOutType.DIFFERENCE,
            amount: isGain ? amount : -amount,
            description: (isGain ? "GAIN" : "LOSS") + " cash transfer",
            paymentType: PaymentMethod.CASH,
            posDateTime: dayjs().toISOString(),
            cashLocation: mapCashLocation(counterParty),
          });
        } catch (error) {
          LogService.info("Failed process money in/out event", { error });
        }
      }
      if (differenceTransferFired) {
        CashFloatModule.setInitialTransferDone(true);
      }
    }

    // A retry after a failed close keeps the destination already transferred to
    const destinationId = alreadySynced
      ? alreadySynced.locationId
      : payload?.staysInDrawer
      ? null
      : payload?.locationId ?? null;

    // If cashier selected a destination, create a transfer-out per currency.
    if (destinationId) {
      const fromLocId = hardwareLocId.value;
      const cfUid = cashfloat.value.uid;
      for (const row of confirmCloseRows.value) {
        if (row.amount <= 0) continue;
        const key = `${row.method}:${row.currency}`;
        if (alreadySynced?.amounts[key] !== undefined) {
          LogService.info(
            `V2 close: skipping destination transfer for ${key}, already synced on a previous submit of cashfloat uid=${cfUid}`
          );
          continue;
        }
        await pushSyncCashTransferV2({
          amount: row.amount / 100,
          currency: row.currency,
          from: fromLocId,
          to: destinationId,
          note: "End of day closure transfer",
          cfUid,
        });
        CashFloatModule.addClosureTransferSynced({
          cashfloatUid: cfUid,
          locationId: destinationId,
          key,
          amount: row.amount,
        });
      }
    }

    // Build the closed cashfloat record and persist it. Full V1 pipeline
    // (cash-out transfers, gain/loss compliance events, receipt printing)
    // will be migrated separately.
    const cashfloatCopy: ICashFloat = JSON.parse(
      JSON.stringify(cashfloat.value)
    );
    cashfloatCopy.closingSummary = {
      manualClosingSummary: manualSummary.value,
      digitalClosingSummary: digitalSummary.value,
    };
    cashfloatCopy.closeDate = dayjs().toISOString();

    CashFloatModule.setCashFloat(cashfloatCopy);

    await pushSyncCashfloat([cashfloatCopy]);

    LogService.info(
      `V2 close: cashfloat ${cashfloatCopy.uid} closed and synced`
    );

    // Print the end-of-day closing bill (V1 parity). The close already
    // succeeded above, so a print failure must not surface as a close error.
    try {
      await prepareAndPrintReceipt(cashfloatCopy);
    } catch (printError) {
      LogService.info("V2 close: closing receipt print failed", {
        printError,
      });
    }

    // Feed the next opening's "expected from last session" like V1 does:
    // the counted amounts stay in the drawer unless they were cashed out
    // to a destination. Leaving the store stale here makes the next
    // opening pre-fill from an outdated previous closure.
    if (destinationId) {
      CashFloatModule.setPreviousCashFloat(null);
    } else {
      CashFloatModule.setPreviousCashFloat({
        uuid: cashfloatCopy.uid,
        closingAmounts: cashPm?.countedAmounts || {},
        isCashedOut: false,
        groupId: cashfloatCopy.groupId,
      });
    }

    // Clear state — same teardown V1 does so the SalesClosedBanner takes over.
    CashFloatModule.setIsClosingCashfloat(false);
    CashFloatModule.setShowClosingSummary(false);
    CashFloatModule.setCashFloat(null);
    // Ensure no order remains when sales are closed — the next till open
    // starts with fresh orders and tab numbering.
    clearOrders();
    const orderMetaStore = useOrderMetaStore();
    orderMetaStore.clearAllOrdersMeta();
    orderMetaStore.resetTabCounter();
    clearReflectedSessions();
    // FIS-508: the closure is fully synced, so re-arm the difference-transfer
    // guard. Without this the flag leaks into the next opening, which then
    // skips its cash transfer sync. Reset only on this success path — a
    // failed close must keep it true so a retried submit can't duplicate the
    // already-synced difference transfers.
    CashFloatModule.setInitialTransferDone(false);
    // The per-currency sync record served its purpose too (it is also
    // keyed by cashfloat uid, so a stale entry could never leak into
    // another closure).
    CashFloatModule.clearGainLossSyncedCurrencies();
    CashFloatModule.clearClosureTransfersSynced();
    await clearDexieDB();
  } catch (error) {
    LogService.info("V2 close: error closing cashfloat", { error });
    NotificationService.showCustomMessage(
      syncedClosureTransfers.value
        ? t("error.close-cashfloat-after-transfer")
        : t("error.close-cashfloat"),
      "error"
    );
  } finally {
    isSubmitting.value = false;
  }
}

// ---------- Closing receipt (V1 parity) ----------
// Builds the closing-summary bill from the same manual/digital summaries the
// screen renders, then hands it to the printer. Ported from V1's
// CloseCashfloat.vue so the V2 close prints the same end-of-day bill.
async function prepareAndPrintReceipt(cashfloat: ICashFloat) {
  const seller = UserModule.user;
  const sellerName = `${seller.firstName} ${seller.lastName}`;
  const groupInfo = await getGroupInfo();

  // Reconciliation is about what the operator counted, so cash rounding is left
  // out here — there is nothing to count against a derived adjustment. It still
  // appears in the payments summary below.
  const paymentMethodSections: IPaymentMethodSection[] = [];
  for (const pm of paymentMethods.value) {
    for (const currency of activeCurrencies.value) {
      // only cash has multi currencies
      if (
        pm.name !== PaymentMethods.METHOD_POS_CASH &&
        currency !== groupCurrency.value
      ) {
        continue;
      }
      const section: IPaymentMethodSection = {
        title: t("payment-method-in-currency", {
          method: t(`payment-method.${pm.name}`),
          currency: currency,
        }),
        lines: [],
      };

      // special case for cash with main currency
      if (
        pm.name === PaymentMethods.METHOD_POS_CASH &&
        currency === groupCurrency.value
      ) {
        section.lines.push({
          label: t("cashfloat.payments"),
          value: (getCustomerAmountIn(pm, currency) / 100).toFixed(2),
          currency: currency,
        });
        section.lines.push({
          label: t("cashfloat.refunds"),
          value: (getRefund(pm, currency) / 100).toFixed(2),
          currency: currency,
        });
        section.lines.push({
          label: t("cashfloat.change"),
          value: (getChange(pm, currency) / 100).toFixed(2),
          currency: currency,
        });

        section.lines.push({
          label: t("cashfloat.settlement"),
          value: (getSettlement(pm, currency) / 100).toFixed(2),
          currency: currency,
          isBold: true,
          hasBorder: true,
        });

        section.lines.push({
          label: t("cashfloat.initial-amount"),
          value: (getInitialAmount(pm, currency) / 100).toFixed(2),
          currency: currency,
          marginTop: true,
        });
        section.lines.push({
          label: t("cashfloat.settlement"),
          value: (getSettlement(pm, currency) / 100).toFixed(2),
          currency: currency,
        });
        if (hasLocation.value) {
          section.lines.push({
            label: t("cashfloat.transfer-in"),
            value: (getTransferIn(pm, currency) / 100).toFixed(2),
            currency: currency,
          });
          section.lines.push({
            label: t("cashfloat.transfer-out"),
            value: (getTransferOut(pm, currency) / 100).toFixed(2),
            currency: currency,
          });
        }

        section.lines.push({
          label: t("cashfloat.expected-amount"),
          value: (getExpectedAmount(pm, currency) / 100).toFixed(2),
          currency: currency,
          isBold: true,
          marginTop: true,
        });
        section.lines.push({
          label: t("cashfloat.counted-amount"),
          value: (getCountedAmount(pm, currency) / 100).toFixed(2),
          currency: currency,
          isBold: true,
        });
        section.lines.push({
          label: t("cashfloat.difference"),
          value: (getDifference(pm, currency) / 100).toFixed(2),
          currency: currency,
          isBold: true,
          hasBorder: true,
        });

        const note = getNote(pm, currency);
        if (note) {
          section.lines.push({
            label: t("cashfloat.note"),
            value: note,
            currency: "",
          });
        }

        paymentMethodSections.push(section);

        continue;
      }

      section.lines.push({
        label: t("cashfloat.initial-amount"),
        value: (getInitialAmount(pm, currency) / 100).toFixed(2),
        currency: currency,
      });
      section.lines.push({
        label: t("cashfloat.payments"),
        value: (getCustomerAmountIn(pm, currency) / 100).toFixed(2),
        currency: currency,
      });
      section.lines.push({
        label: t("cashfloat.refunds"),
        value: (getRefund(pm, currency) / 100).toFixed(2),
        currency: currency,
      });
      section.lines.push({
        label: t("cashfloat.change"),
        value: (getChange(pm, currency) / 100).toFixed(2),
        currency: currency,
      });

      section.lines.push({
        label: t("cashfloat.settlement"),
        value: (getSettlement(pm, currency) / 100).toFixed(2),
        currency: currency,
      });

      if (hasLocation.value) {
        section.lines.push({
          label: t("cashfloat.transfer-in"),
          value: (getTransferIn(pm, currency) / 100).toFixed(2),
          currency: currency,
        });
        section.lines.push({
          label: t("cashfloat.transfer-out"),
          value: (getTransferOut(pm, currency) / 100).toFixed(2),
          currency: currency,
        });
      }

      section.lines.push({
        label: t("cashfloat.expected-amount"),
        value: (getExpectedAmount(pm, currency) / 100).toFixed(2),
        currency: currency,
        isBold: true,
      });
      section.lines.push({
        label: t("cashfloat.counted-amount"),
        value: (getCountedAmount(pm, currency) / 100).toFixed(2),
        currency: currency,
        isBold: true,
      });
      section.lines.push({
        label: t("cashfloat.difference"),
        value: (getDifference(pm, currency) / 100).toFixed(2),
        currency: currency,
        isBold: true,
        hasBorder: true,
      });

      const note = getNote(pm, currency);
      if (note) {
        section.lines.push({
          label: t("cashfloat.note"),
          value: note,
          currency: "",
        });
      }

      paymentMethodSections.push(section);
    }
  }

  const paymentSummarySections: IReceiptSection[] = [];
  const paymentsSummaryLines: IReceiptLine[] = [];
  // Main currency first
  for (const pm of manualSummary.value?.amountsPerPaymentMethods || []) {
    for (const currency of activeCurrencies.value) {
      // skip multi currencies
      if (currency !== groupCurrency.value) {
        continue;
      }

      paymentsSummaryLines.push({
        label: t(`payment-method.${pm.name}`),
        columns: [
          { value: (getCustomerAmountIn(pm, currency) / 100).toFixed(2) },
          { value: (getRefund(pm, currency) / 100).toFixed(2) },
          { value: "0.00" },
          // Tips only come from the card terminal, so no other method can carry one.
          { value: "/" },
        ],
      });
    }
  }

  // Add digital payment methods
  if (digitalSummary.value) {
    paymentsSummaryLines.push({
      label: t(`cashfloat.credit_card`),
      columns: [
        {
          value: (digitalSummary.value.totalCreditCard.payment / 100).toFixed(
            2
          ),
        },
        {
          value: (digitalSummary.value.totalCreditCard.refund / 100).toFixed(2),
        },
        { value: "0.00" },
        {
          value: (
            (digitalSummary.value.totalCreditCard.tip || 0) / 100
          ).toFixed(2),
        },
      ],
    });
    paymentsSummaryLines.push({
      label: t(`cashfloat.gift-cards`),
      columns: [
        {
          value: (digitalSummary.value.totalGiftCard.payment / 100).toFixed(2),
        },
        {
          value: (digitalSummary.value.totalGiftCard.refund / 100).toFixed(2),
        },
        { value: "0.00" },
        { value: "/" },
      ],
    });
  }

  paymentSummarySections.push({
    headers: [`${t("currency")}: ${groupCurrency.value}`],
    lines: paymentsSummaryLines,
  });

  // Cash summary (Multi currency)
  const cashPm = manualSummary.value?.amountsPerPaymentMethods.find(
    (pm) => pm.name === PaymentMethods.METHOD_POS_CASH
  );
  if (cashPm) {
    for (const currency of activeCurrencies.value) {
      // skip main currency
      if (currency === groupCurrency.value) {
        continue;
      }

      const payments = getCustomerAmountIn(cashPm, currency);
      const refunds = getRefund(cashPm, currency);
      const change = getChange(cashPm, currency);

      paymentSummarySections.push({
        headers: [`${t("currency")}: ${currency}`],
        lines: [
          {
            label: t("payment-method.CASH"),
            columns: [
              { value: (payments / 100).toFixed(2) },
              { value: (refunds / 100).toFixed(2) },
              { value: (change / 100).toFixed(2) },
              { value: "/" },
            ],
          },
        ],
      });
    }
  }

  const receiptData: IClosingSummaryReceipt = {
    cashfloatUid: { key: t("receipt.cash-float-id"), value: cashfloat.uid },
    businessId: {
      key: t("field.vat"),
      value: groupInfo.vatNumber || groupInfo.registrationNum,
    },
    openingDate: {
      key: t("receipt.opening-date"),
      value: formatDateTime(manualSummary.value?.createdAt || ""),
    },
    closingDate: {
      key: t("receipt.closing-date"),
      value: formatDateTime(cashfloat.closeDate || ""),
    },
    groupInfo,
    printDate: {
      key: t("receipt.printed-on"),
      value: formatDateTime(dayjs().toString()),
    },
    openendBy: {
      key: t("session.opened"),
      value: openedByName.value,
    },
    closedBy: {
      key: t("session.closed"),
      value: sellerName,
    },
    paymentSummary: {
      title: t("cashfloat.payments-summary"),
      headers: [
        t("receipt.method"),
        t("receipt.payments"),
        t("receipt.refunds"),
        t("receipt.change"),
        t("receipt.tips"),
      ],
      sections: paymentSummarySections,
    },
    paymentMethodSections,
    closureDetailsField: t("cashfloat.closure-details"),
    reconciliationOverviewField: t("cashfloat.reconciliation-overview"),
    salesDetailsField: t("cashfloat.reconciliation-details"),
  };

  if (hasLocation.value) {
    receiptData.device = {
      key: t("settings.device"),
      value: DeviceModule.hardwareLocation?.deviceName || "NA",
    };
  }

  LogService.info(
    `V2 close: Closing summary receipt data prepared for uuid: ${cashfloat.uid}`,
    {
      receiptData,
    }
  );

  printClosingSummaryReceipt(receiptData);
  NotificationService.showCustomMessage(
    t("completeorder.print-receipts-success"),
    "success"
  );
}

// ---------- Render helper for repeated detail rows ----------
const DetailsRow = (props: {
  label: string;
  value: string;
  negative?: boolean;
  bold?: boolean;
  "data-cy"?: string;
}) =>
  h(
    "div",
    {
      class: "flex items-center justify-between px-8 py-4",
      "data-cy": props["data-cy"],
    },
    [
      h(
        "span",
        {
          class: [
            "text-muted-foreground",
            props.bold ? "font-semibold text-foreground" : "",
          ],
        },
        props.label
      ),
      h(
        "span",
        {
          class: [
            props.bold ? "font-semibold" : "",
            props.negative ? "text-red-600" : "",
          ],
        },
        props.value
      ),
    ]
  );

// ---------- Bootstrap ----------
async function bootstrap() {
  try {
    isLoading.value = true;
    // The backend summary only counts persisted orders, so push the queues before loading it.
    summaryMissesQueuedRows.value = !(await drainSyncQueues());
    // Refreshes DeviceModule.locations, which feeds the destination picker.
    DeviceModule.fetchLocations();
    await Promise.all([fetchManualSummary(), fetchDigitalSummary()]);
  } catch (error) {
    LogService.info(`V2 close: error loading summary`, { error });

    const response = (error as { response?: { status?: number } })?.response;
    let message = t("error.general-error");

    if (response?.status === 404) {
      // A missing cash float means a Smeetz admin opened the session
      // which is never saved. Fire a Graylog alert.
      message = t("error.cashfloat-not-found");
      const cashfloatUid = CashFloatModule.cashFloat?.uid;
      const groupId = POSConfigModule.groupId;
      LogService.info(
        `Alert: cash float not found when loading the closing summary. uid=${cashfloatUid} groupId=${groupId}`,
        { params: { cashfloatUid, groupId } },
        LogLevel.Error
      );
    } else if (!response) {
      // The request never reached the server
      message = t("error.load-cashfloat");
    }

    NotificationService.showCustomMessage(message, "error");
    cancelAndReturn();
  } finally {
    isLoading.value = false;
  }
}

async function fetchManualSummary(keepCountsFrom?: IManualClosingSummary) {
  const uuid = CashFloatModule.cashFloat?.uid;
  if (!uuid) return;
  const response = await getManualClosingSummary(uuid);
  manualSummary.value = response;

  if (response.activeCurrencies) {
    response.activeCurrencies.sort((a) => (a === groupCurrency.value ? -1 : 1));
    activeCurrencies.value = response.activeCurrencies;
  }

  // Initialize counted amounts to expected, notes to empty
  for (const pm of response.amountsPerPaymentMethods || []) {
    const kept = keepCountsFrom?.amountsPerPaymentMethods?.find(
      (previous) => previous.name === pm.name
    );
    if (kept) {
      // Only what the cashier counted survives; pre-filled amounts follow the new expected.
      pm.countedAmounts = {};
      for (const [c, amount] of Object.entries(kept.countedAmounts || {})) {
        if (editedCountKeys.has(countedKey(pm.name, c))) {
          pm.countedAmounts[c] = amount;
        }
      }
      pm.notes = { ...kept.notes };
    }
    pm.countedAmounts = pm.countedAmounts || {};
    pm.notes = pm.notes || {};
    for (const c of activeCurrencies.value) {
      if (pm.countedAmounts[c] === undefined) {
        pm.countedAmounts[c] = pm.amounts?.[c] || 0;
      }
      if (pm.notes[c] === undefined) pm.notes[c] = "";
    }
  }

  // Hydrate the editable inputs for every payment method
  for (const pm of response.amountsPerPaymentMethods || []) {
    for (const c of activeCurrencies.value) {
      countedInput.value[countedKey(pm.name, c)] = (
        getCountedAmount(pm, c) / 100
      ).toFixed(2);
    }
  }

  if (activeCurrencies.value.length) {
    selectedCurrency.value = activeCurrencies.value[0];
  }
}

async function fetchDigitalSummary() {
  const uuid = cashfloat.value?.uid;
  if (!uuid) return;
  digitalSummary.value = await getDigitalClosingSummary(uuid);
}

bootstrap();
</script>
